import { Clock, HardDrive } from 'lucide-react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/utils'

// =============================================================================
// Types
// =============================================================================

// Common complexity entry fields used by the widget
export interface ComplexityEntry {
  functionName: string
  timeComplexity?: string | null
  timeComplexityExplanation?: string | null
  spaceComplexity?: string | null
  spaceComplexityExplanation?: string | null
  variableDefinitions?: { variable: string; definition: string }[]
}

// =============================================================================
// VariableDefinitions Component
// =============================================================================

function VariableDefinitions({
  definitions,
}: {
  definitions: { variable: string; definition: string }[]
}) {
  if (definitions.length === 0) return null

  return (
    <div className="text-muted-foreground mt-2 text-sm">
      <span className="italic">where:</span>
      <ul className="mt-1 space-y-0.5 pl-4">
        {definitions.map((d) => (
          <li key={d.variable}>
            <span className="text-foreground font-mono font-medium">
              {d.variable}
            </span>
            {' = '}
            {d.definition}
          </li>
        ))}
      </ul>
    </div>
  )
}

// =============================================================================
// ComplexityCard Component
// =============================================================================

function ComplexityCard({
  label,
  value,
  explanation,
  variableDefinitions,
  icon: Icon,
  accentColor,
}: {
  label: string
  value: string | null | undefined
  explanation?: string | null
  variableDefinitions?: { variable: string; definition: string }[]
  icon: typeof Clock
  accentColor: string
}) {
  if (!value) return null

  return (
    <div className="bg-card border-border relative flex-1 overflow-hidden rounded-xl border">
      {/* Accent bar at top */}
      <div className={cn('h-1', accentColor)} />

      <div className="p-4">
        {/* Label with icon */}
        <div className="mb-2 flex items-center gap-2">
          <Icon
            className={cn(
              'h-4 w-4',
              accentColor.includes('amber')
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-violet-600 dark:text-violet-400',
            )}
          />
          <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
            {label}
          </span>
        </div>

        {/* Complexity value - clean, readable monospace */}
        <div className="text-foreground font-mono text-lg font-semibold">
          {value}
        </div>

        {/* Variable definitions */}
        {variableDefinitions && variableDefinitions.length > 0 && (
          <VariableDefinitions definitions={variableDefinitions} />
        )}

        {/* Explanation (if provided) */}
        {explanation && (
          <article className="prose prose-sm dark:prose-invert text-muted-foreground mt-2 max-w-none leading-relaxed">
            <Markdown remarkPlugins={[remarkGfm]}>{explanation}</Markdown>
          </article>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// ComplexityAnalysis Component
// =============================================================================

export function ComplexityAnalysis({
  complexities,
  className,
}: {
  complexities: ComplexityEntry[] | undefined
  className?: string
}) {
  // Filter to entries that have at least one complexity value
  const entries = complexities?.filter(
    (c) => c.timeComplexity || c.spaceComplexity,
  )
  if (!entries || entries.length === 0) return null

  const isMultiFunction = entries.length > 1

  return (
    <div className={cn('space-y-3', className)}>
      {/* Section header */}
      <div className="flex items-center gap-2">
        <div className="from-primary/80 to-primary h-5 w-1 rounded-full bg-gradient-to-b" />
        <h4 className="text-foreground text-base font-semibold">
          Complexity Analysis
        </h4>
      </div>

      {/* Render each function's complexity */}
      <div className={cn(isMultiFunction && 'space-y-4')}>
        {entries.map((entry, i) => (
          <div key={entry.functionName || i}>
            {/* Function name sub-header (only shown for multi-function) */}
            {isMultiFunction && (
              <div className="text-muted-foreground mb-2 flex items-center gap-2 font-mono text-sm">
                <div className="bg-border h-px flex-1" />
                <span>{entry.functionName || `Function ${i + 1}`}</span>
                <div className="bg-border h-px flex-1" />
              </div>
            )}

            {/* Cards container */}
            <div className="flex gap-3">
              <ComplexityCard
                label="Time"
                value={entry.timeComplexity}
                explanation={entry.timeComplexityExplanation}
                variableDefinitions={entry.variableDefinitions}
                icon={Clock}
                accentColor="bg-gradient-to-r from-amber-500 to-orange-500"
              />
              <ComplexityCard
                label="Space"
                value={entry.spaceComplexity}
                explanation={entry.spaceComplexityExplanation}
                variableDefinitions={entry.variableDefinitions}
                icon={HardDrive}
                accentColor="bg-gradient-to-r from-violet-500 to-purple-500"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
